/**
 * Collection Product List Section JavaScript
 * Handles slider initialization and optional infinite scroll
 */

(function() {
  'use strict';

  function parseAutoplayDelayMs(value, fallbackSeconds) {
    const parsedValue = parseFloat(value);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return fallbackSeconds * 1000;
    }

    const normalizedSeconds = parsedValue > 10 ? parsedValue / 1000 : parsedValue;
    return normalizedSeconds * 1000;
  }

  class CollectionProductList extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.layout = this.dataset.layout;
      this.swiperInstance = null;
      this.isInitialized = false;
      this.originalSlideCount = 0;
      this.infiniteScrollEnabled = this.dataset.infiniteScroll === 'true';
      this.isLoading = false;
      this.observer = null;
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    init() {
      this.initializeLayout();

      if (this.infiniteScrollEnabled && this.layout === 'grid') {
        this.initInfiniteScroll();
      }
    }

    initializeLayout() {
      if (this.layout === 'slider' && !this.isInitialized) {
        this.waitForSwiper();
      }
    }

    waitForSwiper() {
      window.loadThemeSwiper().then(() => {
        this.initSlider();
      }).catch((error) => {
        console.warn('Swiper failed to load for CollectionProductList:', error);
      });
    }

    initSlider() {
      const swiperElement = this.querySelector('.swiper');
      if (!swiperElement || this.isInitialized) return;

      this.originalSlideCount = this.originalSlideCount || swiperElement.querySelectorAll('.swiper-slide').length;
      if (this.originalSlideCount === 0) return;

      const prevButton = this.querySelector('.product-list__nav--prev');
      const nextButton = this.querySelector('.product-list__nav--next');
      const paginationEl = this.querySelector('.product-list__pagination');

      const slidesDesktop = parseInt(this.dataset.slidesDesktop, 10) || 4;
      const slidesTablet = parseInt(this.dataset.slidesTablet, 10) || 3;
      const slidesMobile = parseInt(this.dataset.slidesMobile, 10) || 2;
      const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 16;
      const spaceBetweenMobile = parseInt(this.dataset.spaceBetweenMobile, 10) || 12;
      const speed = parseInt(this.dataset.speed, 10) || 300;
      const showNavigation = this.dataset.showNavigation === 'true';
      const showPagination = this.dataset.showPagination === 'true';
      const loopEnabled = this.dataset.loop !== 'false';
      const loop = loopEnabled && this.originalSlideCount > Math.max(slidesDesktop, slidesTablet, slidesMobile);
      const autoplayEnabled = this.dataset.autoplay === 'true';
      const autoplay = autoplayEnabled
        ? {
            delay: parseAutoplayDelayMs(this.dataset.autoplayDelay, 4),
            disableOnInteraction: false,
            pauseOnMouseEnter: this.dataset.autoplayPauseOnHover !== 'false'
          }
        : false;

      this.swiperInstance = new Swiper(swiperElement, {
        slidesPerView: slidesDesktop,
        spaceBetween: spaceBetween,
        speed: speed,
        loop: loop,
        watchOverflow: true,
        grabCursor: true,
        keyboard: {
          enabled: true
        },
        navigation: showNavigation && prevButton && nextButton
          ? {
              prevEl: prevButton,
              nextEl: nextButton
            }
          : false,
        pagination: showPagination && paginationEl
          ? {
              el: paginationEl,
              clickable: true
            }
          : false,
        autoplay: autoplay,
        breakpoints: {
          0: {
            slidesPerView: slidesMobile,
            spaceBetween: spaceBetweenMobile
          },
          750: {
            slidesPerView: slidesTablet,
            spaceBetween: spaceBetween
          },
          990: {
            slidesPerView: slidesDesktop,
            spaceBetween: spaceBetween
          }
        },
        on: {
          init: () => {
            this.isInitialized = true;
            swiperElement.classList.add('swiper-initialized');
            this.syncNavigationAvailability();
          },
          resize: () => {
            this.syncNavigationAvailability();
          },
          breakpoint: () => {
            this.syncNavigationAvailability();
          },
          lock: () => {
            this.syncNavigationAvailability();
          },
          unlock: () => {
            this.syncNavigationAvailability();
          }
        }
      });

      this.syncNavigationAvailability();
    }

    syncNavigationAvailability() {
      const prevButton = this.querySelector('.product-list__nav--prev');
      const nextButton = this.querySelector('.product-list__nav--next');
      const showNavigation = this.dataset.showNavigation === 'true';

      if (!this.swiperInstance || !prevButton || !nextButton || !showNavigation) {
        this.classList.remove('product-list--can-navigate');
        return;
      }

      const currentSlidesPerView = this.getCurrentSlidesPerView();
      const hasOverflow = this.originalSlideCount > currentSlidesPerView;

      this.classList.toggle('product-list--can-navigate', hasOverflow);
    }

    getCurrentSlidesPerView() {
      const slidesPerView = this.swiperInstance?.params?.slidesPerView;
      const numericSlidesPerView = typeof slidesPerView === 'number'
        ? slidesPerView
        : parseFloat(slidesPerView);

      return Number.isFinite(numericSlidesPerView) && numericSlidesPerView > 0
        ? numericSlidesPerView
        : 1;
    }

    initInfiniteScroll() {
      this.grid = this.querySelector('.product-list__grid');
      this.loadingElement = this.querySelector('.product-list__infinite-scroll-loading');
      this.collectionHandle = this.dataset.collectionHandle;

      if (!this.grid || !this.collectionHandle) return;

      this.totalProducts = parseInt(this.grid.dataset.totalProducts, 10) || 0;
      this.currentPage = 1;
      this.loadedProducts = this.grid.querySelectorAll('.product-list__grid-item').length;

      if (this.loadedProducts >= this.totalProducts) {
        this.loadingElement?.classList.add('is-complete');
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isLoading) {
              this.loadMoreProducts();
            }
          });
        },
        { root: null, rootMargin: '200px', threshold: 0 }
      );

      if (this.loadingElement) {
        this.observer.observe(this.loadingElement);
      }
    }

    async loadMoreProducts() {
      if (this.isLoading || this.loadedProducts >= this.totalProducts) return;

      this.isLoading = true;
      this.loadingElement?.classList.add('is-active');

      const nextPage = this.currentPage + 1;

      try {
        const response = await fetch(`/collections/${this.collectionHandle}?view=infinite-scroll&page=${nextPage}`);
        if (!response.ok) throw new Error('Failed to load products');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newItems = doc.querySelectorAll('.product-list__grid-item');

        if (newItems.length > 0) {
          newItems.forEach((item) => {
            this.grid.appendChild(item.cloneNode(true));
            this.loadedProducts++;
          });

          this.currentPage = nextPage;
          this.grid.dataset.currentPage = nextPage;
        }

        if (this.loadedProducts >= this.totalProducts) {
          this.loadingElement?.classList.add('is-complete');
          this.observer?.disconnect();
        }
      } catch (error) {
        console.error('Error loading more products:', error);
      } finally {
        this.isLoading = false;

        if (this.loadedProducts < this.totalProducts) {
          this.loadingElement?.classList.remove('is-active');
        }
      }
    }

    disconnectedCallback() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.isInitialized = false;
    }
  }

  if (!customElements.get('product-list')) {
    customElements.define('product-list', CollectionProductList);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.target.querySelector('product-list');
      if (section && !section.isInitialized) {
        section.initializeLayout?.();
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      const section = event.target.querySelector('product-list');
      if (section && section.swiperInstance) {
        section.swiperInstance.destroy(true, true);
        section.swiperInstance = null;
        section.isInitialized = false;
      }
    });
  }
})();
